import React, { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { Spinner, Text, makeStyles } from "@fluentui/react-components";
import { themeTokens } from "../../utils/Theming";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeader from "../../blocks/PageHeader";
import Ows from "../../utils/OwsHandler";
import { getUser } from "../../auth/user-slice";
import { useMessage } from "../../blocks/message/MessageContext";
import { getEntityId } from "../../utils/Helpers";
import { buildSearchCondition } from "../../utils/SearchCondition";
import {
  ENTITY_ATTACHED_DOCUMENT,
  ENTITY_CALENDAR_ITEM,
  ENTITY_CANDIDATE,
  ENTITY_CONSULTANT,
  ENTITY_RELATION,
  ENTITY_RELATION_CONTACT,
  ENTITY_TASK,
  ENTITY_VACANCY,
} from "../../utils/Constants";
import SearchInput from "./SearchInput";
import TypeDropdown from "./TypeDropdown";
import SelectedList from "./SelectedList";
import ResultList from "./ResultList";

const ENTITY_CONFIG = {
  UserService: {
    label: "Consultant",
    fields: ["Person.emailPrimary", "Person.firstName", "Person.infix", "Person.lastName"],
    what: { Person: { firstName: 1, infix: 1, lastName: 1, emailPrimary: 1 }, uid: 1 },
    resultField: "person",
    entityId: getEntityId(ENTITY_CONSULTANT),
    separator: " ",
  },
  CandidateService: {
    label: "Candidate",
    fields: ["Person.emailPrimary", "Person.firstName", "Person.infix", "Person.lastName"],
    what: { Person: { firstName: 1, infix: 1, lastName: 1, emailPrimary: 1 }, uid: 1 },
    resultField: "person",
    entityId: getEntityId(ENTITY_CANDIDATE),
    separator: " ",
  },
  RelationService: {
    label: "Customer",
    fields: ["relation"],
    what: { uid: 1, relation: 1 },
    resultField: "relation",
    entityId: getEntityId(ENTITY_RELATION),
    separator: " ",
  },
  RelationContactService: {
    label: "Contact person",
    fields: ["Person.emailPrimary", "Person.firstName", "Person.infix", "Person.lastName"],
    what: { Person: { firstName: 1, infix: 1, lastName: 1, emailPrimary: 1 }, relation: 1, uid: 1 },
    resultField: "person",
    entityId: getEntityId(ENTITY_RELATION_CONTACT),
    separator: " ",
  },
  CalendarService: {
    label: "Calendar item",
    fields: ["title"],
    what: { uid: 1, title: 1 },
    resultField: "title",
    entityId: getEntityId(ENTITY_CALENDAR_ITEM),
    separator: " ",
  },
  VacancyService: {
    label: "Vacancy",
    fields: ["title"],
    what: { uid: 1, title: 1, referenceNr: 1, relation: 1, internalId: 1 },
    resultField: "title",
    entityId: getEntityId(ENTITY_VACANCY),
    separator: null,
  },
  TaskService: {
    label: "Task",
    fields: ["title"],
    what: { uid: 1, title: 1 },
    resultField: "title",
    entityId: getEntityId(ENTITY_TASK),
    separator: " ",
  },
  AttachedDocumentsService: {
    label: "Document",
    fields: ["fileName", "subject"],
    what: { uid: 1, fileName: 1, subject: 1 },
    resultField: "fileName",
    entityId: getEntityId(ENTITY_ATTACHED_DOCUMENT),
    separator: " ",
  },
};

const useStyles = makeStyles({
  sectionHeader: { backgroundColor: themeTokens.colorSectionHeaderBackground, padding: "20px" },
  inputField: { marginBottom: "12px" },
  input: { width: "100%" },
  sectionTitle: { marginBottom: "16px", color: themeTokens.colorNeutralForeground1 },
  resultsContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 250px)",
    padding: "20px",
  },
  selectedListContainer: {
    overflowY: "auto",
    maxHeight: "50%",
    marginBottom: "20px",
  },
  resultListContainer: {
    flex: 1,
    overflowY: "auto",
  },
});

const EmailLinkForm = () => {
  const styles = useStyles();
  const user = useSelector(getUser);
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const [emailId, setEmailId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<keyof typeof ENTITY_CONFIG | "">("");
  const [searchTerm, setSearchTerm] = useState("");
  const [allResults, setAllResults] = useState<any[]>([]);
  const [selectedResults, setSelectedResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const message = Office.context.mailbox.item;
    const messageId = message?.internetMessageId;
    if (!messageId) return;

    (async () => {
      const data = await Ows.handleRequest("Otys.Services.EmailService.getList", [
        {
          what: { uid: 1 },
          condition: {
            type: "AND",
            items: [
              { type: "COND", field: "userId", op: "EQ", param: user.id },
              { type: "COND", field: "messageOid", op: "EQ", param: messageId },
            ],
          },
          limit: 1,
        },
      ]);
      const resolvedEmailId = data?.response?.[0]?.uid;
      if (resolvedEmailId) {
        setEmailId(resolvedEmailId);
        fetchBindings(resolvedEmailId);
      }
      setLoading(false);
    })();
  }, [user.id]);

  const fetchEntityItems = async (serviceKey: string) => {
    const config = ENTITY_CONFIG[serviceKey];
    setSearching(true);
    try {
      const response = await Ows.handleRequest(`Otys.Services.${serviceKey}.getList`, [
        { what: config.what, limit: 20 },
      ]);
      const data = response.response || [];

      const mapped = data.map((item: any) => {
        let label = "";
        if (config.resultField === "person") {
          const person = item.Person || {};
          const name = `${person.firstName || ""} ${person.infix || ""} ${person.lastName || ""}`.trim();
          const email = person.emailPrimary;
          label = name || email || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
        } else if (config.resultField === "relation") {
          label = item.relation || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
        } else {
          label = item[config.resultField] || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
        }

        return {
          uid: item.uid,
          label,
          type: config.label,
          entityId: config.entityId,
        };
      });

      setAllResults(mapped);
    } finally {
      setSearching(false);
    }
  };

  const fetchBindings = async (emailId: string | number) => {
    const data = await Ows.handleRequest("Otys.Services.EmailService.getDetail", [
      emailId,
      { Bindings: { uid: 1, entityId: 1, entity: 1, title: 1 } },
    ]);
    const bindingsObject = data.response?.Bindings || {};
    const bindingsArray = Object.entries(bindingsObject);

    const mapped = bindingsArray.map(([id, binding]: [string | number, any]) => {
      const entry = Object.entries(ENTITY_CONFIG).find(([, config]) => config.entityId === binding.entityId);
      if (!entry) return null;
      const [, config] = entry;

      return {
        uid: binding.uid,
        label: binding.title || `Unnamed ${config.label} (${binding.uid.toString().slice(-4)})`,
        type: config.label,
        entityId: binding.entityId,
        bindingId: id.toString(),
      };
    });

    setSelectedResults(mapped.filter(Boolean));
  };

  const handleCheckboxChange = async (item: any, fromSelected: boolean) => {
    if (!emailId) {
      showMessage("error", "Email ID is not available.");
      return;
    }

    const isAlreadyLinked = !!item.bindingId;

    if (fromSelected) {
      setSelectedResults((prev) => prev.filter((i) => i.uid !== item.uid));

      const config = ENTITY_CONFIG[entityType as keyof typeof ENTITY_CONFIG];
      if (config?.label === item.type) {
        setAllResults((prev) => [...prev, item]);
      }

      if (isAlreadyLinked) {
        try {
          await Ows.handleRequest("Otys.Services.EmailService.update", [
            emailId,
            { Bindings: { DELETE: [item.bindingId] } },
          ]);
          showMessage("info", `Unlinked from ${item.type}`);
        } catch {
          showMessage("error", `Failed to unlink ${item.label}`);
        }
      }
    } else {
      setAllResults((prev) => prev.filter((i) => i.uid !== item.uid));
      setSelectedResults((prev) => [...prev, item]);

      if (!isAlreadyLinked) {
        try {
          await Ows.handleRequest("Otys.Services.EmailService.update", [
            emailId,
            { Bindings: { ADD: [{ uid: item.uid, entityId: item.entityId }] } },
          ]);
          showMessage("success", `Linked to ${item.type}`);
        } catch {
          showMessage("error", `Failed to link ${item.label}`);
        }
      }
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(async (search: string, serviceKey: string) => {
        const config = ENTITY_CONFIG[serviceKey];
        const condition = buildSearchCondition(config, search);
        setSearching(true);
        try {
          const response = await Ows.handleRequest(`Otys.Services.${serviceKey}.getList`, [
            { what: config.what, limit: 20, condition },
          ]);
          const data = response.response || [];
          const mapped = data.map((item: any) => {
            let label = "";
            if (config.resultField === "person") {
              const person = item.Person || {};
              const name = `${person.firstName || ""} ${person.infix || ""} ${person.lastName || ""}`.trim();
              label = name || person.emailPrimary || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
            } else if (config.resultField === "relation") {
              label = item.relation || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
            } else {
              label = item[config.resultField] || `Unnamed ${config.label} (${item.uid.toString().slice(-4)})`;
            }
            return {
              uid: item.uid,
              label,
              type: config.label,
              entityId: config.entityId,
            };
          });
          setAllResults(mapped);
        } finally {
          setSearching(false);
        }
      }, 400),
    []
  );

  useEffect(() => {
    if (!entityType || !searchTerm.trim()) return () => {};
    debouncedSearch(searchTerm, entityType);
    return () => debouncedSearch.cancel();
  }, [searchTerm, entityType, debouncedSearch]);

  useEffect(() => {
    if (!entityType || searchTerm.trim()) return;
    fetchEntityItems(entityType);
  }, [searchTerm, entityType]);

  return (
    <>
      <PageHeader onBack={() => navigate(-1)} />
      <div className={styles.sectionHeader}>
        <Text weight="semibold" size={400} block className={styles.sectionTitle}>
          Link mail to related items
        </Text>
        <div className={styles.inputField}>
          <TypeDropdown
            value={entityType}
            onChange={(selected) => {
              setEntityType(selected);
              setSearchTerm("");
              setAllResults([]);
              fetchEntityItems(selected);
            }}
            options={Object.entries(ENTITY_CONFIG).map(([key, cfg]) => ({ key, label: cfg.label }))}
            className={styles.input}
          />
        </div>
        <div className={styles.inputField}>
          <SearchInput value={searchTerm} onChange={(value) => setSearchTerm(value)} className={styles.input} />
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className={styles.resultsContainer}>
          <SelectedList
            items={selectedResults}
            onUnselect={(item) => handleCheckboxChange(item, true)}
            className={styles.selectedListContainer}
          />
          {searching ? (
            <Spinner />
          ) : (
            <ResultList
              items={allResults}
              onSelect={(item) => handleCheckboxChange(item, false)}
              className={styles.resultListContainer}
            />
          )}
        </div>
      )}
    </>
  );
};

export default EmailLinkForm;
